/**
 * Image wrapper component with lazy loading and error fallback.
 *
 * Renders an image with `loading="lazy"` and `decoding="async"` for
 * performance. On load failure, displays a lavender placeholder div.
 *
 * @module landing/components/ImagePlaceholder
 */

import React, { useState } from 'react';

export interface ImagePlaceholderProps {
  /** Image source URL. */
  src: string;
  /** Alt text for accessibility. */
  alt: string;
  /** CSS aspect-ratio value (default: "16/9"). */
  aspectRatio?: string;
}

/**
 * Lazy-loaded image with a fallback placeholder on error.
 */
export function ImagePlaceholder({
  src,
  alt,
  aspectRatio = '16/9',
}: ImagePlaceholderProps): React.JSX.Element {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <div
        className="landing-image-placeholder__fallback"
        style={{ aspectRatio }}
        role="img"
        aria-label={alt}
        data-testid="image-fallback"
      />
    );
  }

  return (
    <div className="landing-image-placeholder" style={{ aspectRatio }}>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        className="landing-image-placeholder__img"
        onError={() => setHasError(true)}
      />
    </div>
  );
}
